import { useEffect, useRef, useCallback } from "react";

interface UseInfiniteScrollOptions {
  hasMore: boolean;
  loading: boolean;
  onLoadMore: () => void;
  threshold?: number; // Distance from top to trigger load (default: 50px)
}

export function useInfiniteScroll({
  hasMore,
  loading,
  onLoadMore,
  threshold = 50,
}: UseInfiniteScrollOptions) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isLoadingRef = useRef(false);
  const onLoadMoreRef = useRef(onLoadMore);
  const hasMoreRef = useRef(hasMore);
  const loadingRef = useRef(loading);

  const armedRef = useRef(true);

  // Optional: small hysteresis (re-arm only after user scrolls a bit away)
  const rearmDistance = threshold * 2;

  useEffect(() => {
    onLoadMoreRef.current = onLoadMore;
    hasMoreRef.current = hasMore;
    loadingRef.current = loading;
    if (!loading) {
      isLoadingRef.current = false;
    }
  }, [onLoadMore, hasMore, loading]);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const top = container.scrollTop;

    if (top >= rearmDistance) {
      armedRef.current = true;
    }

    if (
      top < threshold && // near the top
      armedRef.current && // only on edge crossing
      !loadingRef.current &&
      hasMoreRef.current &&
      !isLoadingRef.current
    ) {
      isLoadingRef.current = true; // lock
      armedRef.current = false; // disarm until user scrolls away
      onLoadMoreRef.current();
    }
  }, [threshold, rearmDistance]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  return scrollContainerRef;
}
